var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
import React, { useCallback, useEffect, useRef, useState } from "react";
import { Button, FormControl, FormHelperText, FormLabel, HStack, Input, Select, Spinner, Switch, Textarea, useToast, VStack } from "@chakra-ui/react";
import Markdown from "react-markdown";
import { ThemedMarkdownRenderer } from "../lib/ThemedMarkdownRenderer";
export const Form = ({ formId, onLoad }) => {
    const [formDefinition, setFormDefinition] = useState(null);
    const [inputs, setInputs] = useState([]);
    const [loading, setLoading] = useState(false);
    const toast = useToast();
    const formRef = useRef(null);
    const initializeFormTracking = useCallback(() => {
        if (typeof window.Matomo !== "object" || typeof window.Matomo.trackForm !== "function")
            return;
        if (!formRef.current)
            return;
        const Matomo = window.Matomo;
        Matomo.trackForm(formRef.current);
    }, [formRef]);
    useEffect(() => {
        const loadForm = () => __awaiter(void 0, void 0, void 0, function* () {
            if (formId == null)
                return;
            const response = yield fetch("/form/" + formId);
            const definition = yield response.json();
            setFormDefinition(definition);
            setInputs(definition.fields.map(field => field.type === "select" && field.options[0] ? field.options[0] : ""));
            if (onLoad) {
                onLoad(definition);
            }
            initializeFormTracking();
        });
        loadForm();
    }, [formId, initializeFormTracking]);
    const submit = () => __awaiter(void 0, void 0, void 0, function* () {
        var _a, _b;
        if (formId == null || !formDefinition)
            return;
        setLoading(true);
        const response = yield fetch("/form/" + formId + "/send", {
            method: "POST",
            headers: {
                "Content-Type": "application/json"
            },
            body: JSON.stringify({ inputs })
        });
        setLoading(false);
        const _paq = window._paq;
        if (response.status !== 200) {
            if (Array.isArray(_paq)) {
                _paq.push(["trackEvent", "Form", "Error", (_a = formDefinition.heading) !== null && _a !== void 0 ? _a : String(formId)]);
            }
            return toast({
                status: "error",
                title: "Es gab ein Problem beim Absenden Ihrer Eingabe.",
                description: "Bitte prüfen Sie Ihre Verbindung und versuchen Sie es erneut."
            });
        }
        if (Array.isArray(_paq)) {
            _paq.push(["trackEvent", "Form", "Submit", (_b = formDefinition.heading) !== null && _b !== void 0 ? _b : String(formId)]);
        }
        return toast({
            status: "success",
            title: "Erfolgreich",
            isClosable: true,
            description: formDefinition.successMessage,
            duration: null
        });
    });
    if (!formDefinition) {
        return React.createElement(Spinner, null);
    }
    return (React.createElement("form", { onSubmit: event => {
            event.preventDefault();
            submit();
        }, style: { width: "100%" }, ref: formRef },
        React.createElement(VStack, { align: "stretch", spacing: 4, width: "100%" },
            formDefinition.fields.map((field, index) => {
                if (field.type === "text") {
                    return (React.createElement(FormControl, { key: index, isRequired: field.required },
                        React.createElement(FormLabel, null, field.label),
                        field.multiline ? (React.createElement(Textarea, { name: field.label, value: inputs[index], onChange: event => setInputs(inputs.map((input, inputIndex) => inputIndex === index ? event.target.value : input)) })) : (React.createElement(Input, { name: field.label, value: inputs[index], onChange: event => setInputs(inputs.map((input, inputIndex) => inputIndex === index ? event.target.value : input)) }))));
                }
                if (field.type === "select") {
                    return (React.createElement(FormControl, { key: index },
                        React.createElement(FormLabel, null, field.label),
                        React.createElement(Select, { name: field.label, value: inputs[index], onChange: event => setInputs(inputs.map((input, inputIndex) => inputIndex === index ? event.target.value : input)) }, field.options.map((option, optionIndex) => (React.createElement("option", { key: optionIndex, value: option }, option))))));
                }
                if (field.type === "checkbox") {
                    return (React.createElement(FormControl, { key: index, isRequired: field.required },
                        React.createElement(HStack, null,
                            React.createElement(Switch, { name: field.label, isChecked: inputs[index] === "true", onChange: event => setInputs(inputs.map((input, inputIndex) => inputIndex === index ? (event.target.checked ? "true" : "false") : input)) }),
                            React.createElement(FormLabel, null, field.label)),
                        field.help && (React.createElement(FormHelperText, null,
                            React.createElement(Markdown, { components: ThemedMarkdownRenderer() }, field.help)))));
                }
                if (field.type === "markdownText" && field.markdownText !== "") {
                    return (React.createElement(Markdown, { components: ThemedMarkdownRenderer(), key: index }, field.markdownText));
                }
                return null;
            }),
            React.createElement(Button, { type: "submit", isLoading: loading }, "Absenden"))));
};
